import { useEffect, useMemo, useState } from 'react'
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Spinner,
  Tooltip,
  tokens,
} from '@fluentui/react-components'
import { apiFetchJson } from '../lib/apiFetch'
import { useLicenseFilters } from '../lib/useLicenseFilters'
import { useProblemFilters } from '../hooks/useProblemFilters'
import {
  buildRows,
  filterPersonRows,
  hasDuplicateLicense,
  isFullyExternal,
  isMissingOwner,
  isNotSso,
  isServiceBlocked,
  isStale,
  licenseTypeDisplayName,
} from '../lib/personRows'
import LicenseFilterBar from './LicenseFilterBar'
import ProblemFilterBar from './ProblemFilterBar'
import ResolutionBadge from './ResolutionBadge'
import StatusBadge from './StatusBadge'
import Lozenge from './Lozenge'
import type { LicensesResponse, LicenseTypeConfig, LicenseTypesResponse, ServiceConfigResponse } from '../types'

const DEFAULT_STALE_LOGIN_DAYS = 90

/** Per-service "who has a license here" view — the default tab on /services/:serviceId, using
 * the same filter bar as pages/Licenses.tsx (Status/License type/Show without licenses), plus
 * the same problem/role filter bar as User links (see hooks/useProblemFilters.ts) — same filters
 * everywhere, not a separate set invented per tab. Shows license names rather than icons — with
 * only one service in view there's no need for the main page's compact icon-per-cell layout.
 * Unlike the all-services page, rows are grouped by raw account (groupByRawEmail) rather than by
 * resolved person — this tab is for browsing this service's own accounts, so a manually-linked
 * account should keep showing as itself (with a "Manually linked" badge), not disappear into the
 * target person's row. */
export default function ServiceLicensesTab(props: { serviceId: string; refreshKey: number }) {
  const { serviceId, refreshKey } = props
  const [data, setData] = useState<LicensesResponse | null>(null)
  const [config, setConfig] = useState<Record<string, LicenseTypeConfig>>({})
  const [staleLoginDays, setStaleLoginDays] = useState(DEFAULT_STALE_LOGIN_DAYS)
  const [error, setError] = useState<string | null>(null)
  const { filters, updateFilters } = useLicenseFilters(`licenses-filters-${serviceId}`)
  const [searchText, setSearchText] = useState('')

  useEffect(() => {
    Promise.all([
      apiFetchJson<LicensesResponse>(`/api/licenses?serviceId=${serviceId}`),
      apiFetchJson<LicenseTypesResponse>(`/api/license-types?serviceId=${serviceId}`),
    ])
      .then(([licenses, types]) => {
        setData(licenses)
        setConfig(Object.fromEntries(types.types.map(t => [t.licenseType, t])))
      })
      .catch(e => setError(String(e)))
  }, [serviceId, refreshKey])

  useEffect(() => {
    apiFetchJson<ServiceConfigResponse>('/api/service-config')
      .then(res => setStaleLoginDays(res.config[serviceId]?.staleLoginDays ?? DEFAULT_STALE_LOGIN_DAYS))
      .catch(() => {})
  }, [serviceId])

  const configByService = useMemo(() => ({ [serviceId]: config }), [serviceId, config])
  const rows = useMemo(
    () => (data ? buildRows(data.persons, data.results, { groupByRawEmail: true }) : []),
    [data],
  )
  // The problem-filter chips must count this service's own accounts, not every synced person —
  // `rows` also contains zero-license people (so "Show users without licenses" works), which made
  // the per-service "All" chip show the whole directory (e.g. All (814) on a 135-license service).
  const scopedRows = useMemo(() => rows.filter(row => (row.byService[serviceId]?.length ?? 0) > 0), [rows, serviceId])
  const { problemFilters, problemFilter, setProblemFilter, roleFilter, setRoleFilter, activeFilter, hasRoles, duplicateKeys } =
    useProblemFilters(scopedRows, staleLoginDays)
  const filteredRows = useMemo(() => {
    const text = searchText.trim().toLowerCase()
    return filterPersonRows(rows, filters)
      .filter(activeFilter.predicate)
      .filter(row => !roleFilter || row.role === roleFilter)
      .filter(row => !text || row.rawEmail.toLowerCase().includes(text) || row.personDisplayName.toLowerCase().includes(text))
  }, [rows, filters, activeFilter, roleFilter, searchText])

  if (error) return <p>Error: {error}</p>
  if (!data) return <Spinner label="Loading licenses..." />

  return (
    <div>
      <LicenseFilterBar
        serviceIds={[serviceId]}
        results={data.results}
        configByService={configByService}
        filters={filters}
        onChange={updateFilters}
        hideSearch
      />
      <ProblemFilterBar
        rows={scopedRows}
        searchText={searchText}
        onSearchChange={setSearchText}
        problemFilters={problemFilters}
        problemFilter={problemFilter}
        onProblemFilterChange={setProblemFilter}
        roleFilter={roleFilter}
        onRoleFilterChange={setRoleFilter}
        hasRoles={hasRoles}
      />

      <Table style={{ width: '100%' }}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Person</TableHeaderCell>
            <TableHeaderCell style={{ width: 140 }}>Resolution</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell style={{ width: 130 }}>Department</TableHeaderCell>
            <TableHeaderCell style={{ width: 170 }}>Responsible</TableHeaderCell>
            <TableHeaderCell style={{ width: 130 }}>Login type</TableHeaderCell>
            <TableHeaderCell style={{ width: 150 }}>Last login</TableHeaderCell>
            <TableHeaderCell>Licenses</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredRows.map(row => {
            // Excludes the synthetic "no trackable license" row a module can emit to keep a
            // free-tier-only account visible for role/login-type reporting — not a real license,
            // so it shouldn't show up in the Licenses column.
            const assignments = (row.byService[serviceId] ?? []).filter(a => !a.excludeFromSeatCount)
            const hasDuplicate = hasDuplicateLicense(row, duplicateKeys)
            return (
              <TableRow key={row.rawEmail}>
                <TableCell>
                  {row.resolutionKind === 'manual' ? (
                    <>
                      <div>{row.rawEmail}</div>
                      <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>
                        Linked to {row.personDisplayName}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>{row.personDisplayName}</div>
                      <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>{row.rawEmail}</div>
                    </>
                  )}
                  {row.role && (
                    <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>{row.role}</div>
                  )}
                </TableCell>
                <TableCell>
                  <ResolutionBadge kind={row.resolutionKind} />
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                    <StatusBadge status={row.entraStatus} external={isFullyExternal(row)} />
                    {isServiceBlocked(row) && (
                      <Tooltip
                        content={`Disabled in the vendor's own system (${row.accountStatus}) but still holds this license — a wasted seat.`}
                        relationship="description"
                        withArrow
                      >
                        <Lozenge color="danger">Blocked in service</Lozenge>
                      </Tooltip>
                    )}
                    {isMissingOwner(row) && (
                      <Tooltip
                        content="No department and no responsible person set — this account doesn't count as owned by anyone. Use Mark external/shared (or set a department in Entra) to fix it."
                        relationship="description"
                        withArrow
                      >
                        <Lozenge color="warning">No owner</Lozenge>
                      </Tooltip>
                    )}
                    {isNotSso(row) && (
                      <Tooltip
                        content={`Signs in via "${row.loginType}" rather than the org's SSO — bypasses centralized MFA/deprovisioning.`}
                        relationship="description"
                        withArrow
                      >
                        <Lozenge color="danger">Not SSO</Lozenge>
                      </Tooltip>
                    )}
                    {isStale(row, staleLoginDays) && (
                      <Tooltip
                        content={`No login in the last ${staleLoginDays} days${row.lastLoginAt ? ` (last: ${row.lastLoginAt})` : ' (never logged in)'} — a likely wasted seat.`}
                        relationship="description"
                        withArrow
                      >
                        <Lozenge color="warning">Stale login</Lozenge>
                      </Tooltip>
                    )}
                    {hasDuplicate && (
                      <Tooltip
                        content="This person holds the same license type more than once on this service (typically a primary email plus an alias) — a likely wasted/duplicate seat."
                        relationship="description"
                        withArrow
                      >
                        <Lozenge color="danger">Duplicate</Lozenge>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
                <TableCell>{row.department ?? '—'}</TableCell>
                <TableCell>
                  <div>{row.responsibleDisplayName ?? '—'}</div>
                  {row.responsibleNeedsReview && (
                    <Tooltip
                      content="The named responsible person is now disabled or removed in Entra — this override needs to be revisited."
                      relationship="description"
                      withArrow
                    >
                      <Lozenge color="danger">Responsible blocked</Lozenge>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{row.loginType ?? '—'}</TableCell>
                <TableCell>{row.lastLoginAt ? row.lastLoginAt.replace('T', ' ') : '—'}</TableCell>
                <TableCell>
                  {assignments.map(a => licenseTypeDisplayName(a.licenseType, configByService)).join(', ')}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      {filteredRows.length === 0 && <p>No one matches the current filters.</p>}
    </div>
  )
}
